/**
 * Reuse of a film shared with Allow reuse (docs/SPEC.md §11.2 (3), §12,
 * §14.5; P27-T01), on the public review page: for a signed-in person, the
 * film's scenes (each can be taken into one of their own ads: a change on that
 * ad, which its page then follows) and the templates made from it (each can be
 * copied into their own templates). Signed out, a sentence and a sign-in link.
 * The scenes' and the templates' pictures are frames the player draws in the
 * browser from their manifests (P28-T10): the film's current version's,
 * which the link lets the receiver read, and each template's.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAds } from "../api/ads";
import { ApiError } from "../api/client";
import {
  useCopySharedTemplate,
  useReuseOffer,
  useTakeSharedScene,
} from "../api/share";
import type { Aspect, ReuseOffer } from "../api/types";
import { REUSE } from "../copy/outputs";
import { frameSize } from "../film/frames";
import { useManifestFrame } from "../film/useFrame";
import { Button, Card, Field, Select, Skeleton } from "../ui";

/** A scene's picture: a grid cell, drawn at 360 px on its short side. */
const SCENE_SHORT = 360;
/** A template's picture: 48 px high, drawn at 144. */
const TEMPLATE_SHORT = 144;

/** Scene `sceneId` (the first built when null) of the film at
 *  `manifestUrl`, framed in the browser; the scene's number until then. */
function Frame({
  manifestUrl,
  sceneId,
  aspect,
  short,
  label,
  className,
  testId,
}: {
  manifestUrl: string | null;
  sceneId: string | null;
  aspect: Aspect;
  short: number;
  label: string;
  className: string;
  testId: string;
}) {
  const frame = useManifestFrame(
    manifestUrl,
    sceneId,
    frameSize(aspect, short),
    !!manifestUrl,
  );
  const ratio = aspect.replace(":", " / ");
  return frame ? (
    <img
      src={frame}
      alt={label}
      className={`${className} rounded bg-slate-100 object-cover`}
      style={{ aspectRatio: ratio }}
      data-testid={testId}
    />
  ) : (
    <div
      className={`${className} flex items-center justify-center rounded bg-slate-100 text-xs text-slate-500`}
      style={{ aspectRatio: ratio }}
    />
  );
}

function failure(error: unknown): string {
  const code = error instanceof ApiError ? error.code : null;
  return (code && REUSE.failed[code]) || REUSE.failedOther;
}

export function ReusePanel({ token }: { token: string }) {
  const offer = useReuseOffer(token);
  if (offer.isPending) {
    return (
      <Card aria-busy="true" className="flex flex-col gap-3 p-6">
        <Skeleton height={18} width="40%" />
        <Skeleton height={120} />
      </Card>
    );
  }
  if (offer.isError) {
    const status = offer.error instanceof ApiError ? offer.error.status : null;
    if (status !== 401) return null;
    const back = `/r/${token}`;
    return (
      <Card
        className="flex flex-wrap items-center gap-3 p-6"
        data-testid="reuse-panel"
      >
        <p className="m-0 text-sm text-slate-700">{REUSE.signIn}</p>
        <Link
          className="text-sm font-semibold"
          to={`/login?return_to=${encodeURIComponent(back)}`}
        >
          {REUSE.signInLink}
        </Link>
      </Card>
    );
  }
  return <Offer token={token} offer={offer.data} />;
}

function Offer({ token, offer }: { token: string; offer: ReuseOffer }) {
  const navigate = useNavigate();
  const ads = useAds();
  const take = useTakeSharedScene(token);
  const copy = useCopySharedTemplate(token);
  const ready = (ads.data?.items ?? []).filter((a) => a.state === "ready");
  const [target, setTarget] = useState<string>("");
  const into = target || ready[0]?.id || "";
  const [busy, setBusy] = useState<string | null>(null);
  const [copied, setCopied] = useState<Record<string, string>>({});
  const aspect = (offer.aspect ?? "16:9") as Aspect;
  const portrait = offer.aspect === "9:16";

  return (
    <Card className="flex flex-col gap-5 p-6" data-testid="reuse-panel">
      <div>
        <h2 className="tb-h-row m-0">{REUSE.title}</h2>
        <p className="m-0 mt-1 text-sm text-slate-500">{REUSE.meta}</p>
      </div>

      {offer.scenes.length > 0 ? (
        <section className="flex flex-col gap-3" aria-label={REUSE.scenes}>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h3 className="m-0 text-sm font-semibold text-slate-800">
              {REUSE.scenes}
            </h3>
            {ready.length > 0 ? (
              <Field label={REUSE.into}>
                <Select
                  value={into}
                  onChange={(e) => setTarget(e.target.value)}
                  className="min-w-[12rem]"
                >
                  {ready.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.title}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : ads.data ? (
              <p className="m-0 text-sm text-slate-600">
                {REUSE.noAds} <Link to="/new">{REUSE.makeAd}</Link>
              </p>
            ) : null}
          </div>
          <ul
            className={`m-0 grid list-none gap-3 p-0 ${
              portrait
                ? "grid-cols-2 sm:grid-cols-5"
                : "grid-cols-2 sm:grid-cols-3"
            }`}
          >
            {offer.scenes.map((scene, i) => (
              <li
                key={scene.id}
                className="flex flex-col gap-2"
                data-testid={`reuse-scene-${scene.id}`}
              >
                <Frame
                  manifestUrl={offer.manifest_url}
                  sceneId={scene.id}
                  aspect={aspect}
                  short={SCENE_SHORT}
                  label={scene.name}
                  className="w-full"
                  testId={`reuse-frame-${scene.id}`}
                />
                <div className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="font-semibold text-slate-800">
                    {i + 1}. {scene.name}
                  </span>
                  {scene.seconds !== null ? (
                    <span className="whitespace-nowrap text-xs text-slate-500">
                      {REUSE.seconds(scene.seconds)}
                    </span>
                  ) : null}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="mt-auto"
                  disabled={!into || busy !== null}
                  aria-busy={busy === scene.id}
                  aria-label={`${REUSE.use}: ${scene.name}`}
                  onClick={() => {
                    setBusy(scene.id);
                    take.mutate(
                      { ad_id: into, scene_id: scene.id },
                      {
                        onSuccess: (made) => navigate(`/ads/${made.ad_id}`),
                        onSettled: () => setBusy(null),
                      },
                    );
                  }}
                >
                  {busy === scene.id ? REUSE.using : REUSE.use}
                </Button>
              </li>
            ))}
          </ul>
          {take.isError ? (
            <p role="alert" className="m-0 text-sm text-slate-700">
              {failure(take.error)}
            </p>
          ) : null}
        </section>
      ) : null}

      {offer.templates.length > 0 ? (
        <section className="flex flex-col gap-3" aria-label={REUSE.templates}>
          <h3 className="m-0 text-sm font-semibold text-slate-800">
            {REUSE.templates}
          </h3>
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {offer.templates.map((t) => (
              <li
                key={t.id}
                className="flex flex-wrap items-center gap-3"
                data-testid={`reuse-template-${t.id}`}
              >
                <Frame
                  manifestUrl={t.manifest_url}
                  sceneId={null}
                  aspect={t.aspect ?? aspect}
                  short={TEMPLATE_SHORT}
                  label=""
                  className="h-12 shrink-0"
                  testId={`reuse-template-frame-${t.id}`}
                />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="text-sm font-semibold text-slate-800">
                    {t.name}
                  </span>
                  {t.sentence ? (
                    <span className="text-xs text-slate-500">{t.sentence}</span>
                  ) : null}
                </div>
                {copied[t.id] ? (
                  <span className="text-sm text-slate-700" role="status">
                    {REUSE.copied}{" "}
                    <Link
                      className="font-semibold"
                      to={`/new?template=${encodeURIComponent(copied[t.id])}`}
                    >
                      {REUSE.makeFrom}
                    </Link>
                  </span>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={busy !== null}
                    aria-busy={busy === t.id}
                    aria-label={`${REUSE.copy}: ${t.name}`}
                    onClick={() => {
                      setBusy(t.id);
                      copy.mutate(t.id, {
                        onSuccess: (mine) =>
                          setCopied((old) => ({ ...old, [t.id]: mine.id })),
                        onSettled: () => setBusy(null),
                      });
                    }}
                  >
                    {busy === t.id ? REUSE.copying : REUSE.copy}
                  </Button>
                )}
              </li>
            ))}
          </ul>
          {copy.isError ? (
            <p role="alert" className="m-0 text-sm text-slate-700">
              {failure(copy.error)}
            </p>
          ) : null}
        </section>
      ) : null}
    </Card>
  );
}
